/**
 * Imperial / metric unit conversion + a tiny cross-component toggle store.
 *
 * The toggle is hydration-safe: the server (and the first client render)
 * always report "imperial" so markup matches, and the real, persisted
 * preference is applied in a useEffect after mount via useSyncExternalStore.
 */
import { useEffect, useSyncExternalStore } from "react";

export type UnitSystem = "imperial" | "metric";

const STORAGE_KEY = "asd-unit-system";

let currentUnit: UnitSystem = "imperial";
let hydrated = false;
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot(): UnitSystem {
  return currentUnit;
}

function getServerSnapshot(): UnitSystem {
  return "imperial";
}

export function setUnitSystem(unit: UnitSystem) {
  currentUnit = unit;
  hydrated = true;
  try {
    window.localStorage.setItem(STORAGE_KEY, unit);
  } catch {
    // localStorage unavailable (private mode, SSR) — ignore.
  }
  emitChange();
}

function hydrateFromStorage() {
  if (hydrated) return;
  hydrated = true;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "metric" || stored === "imperial") {
      currentUnit = stored;
      emitChange();
    }
  } catch {
    // ignore
  }
}

/** Read + subscribe to the current unit system; defaults to imperial on the server. */
export function useUnitSystem(): [UnitSystem, (unit: UnitSystem) => void] {
  const unit = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  useEffect(() => {
    hydrateFromStorage();
  }, []);
  return [unit, setUnitSystem];
}

// --- Conversions ---

export const inToCm = (n: number) => n * 2.54;
export const inToMm = (n: number) => n * 25.4;
export const lbsToKg = (n: number) => n * 0.45359237;
export const milesToKm = (n: number) => n * 1.609344;
export const ftLbToNm = (n: number) => n * 1.35581795;
export const psiToKpa = (n: number) => n * 6.89476;
export const fToC = (n: number) => ((n - 32) * 5) / 9;
/** MPG (US gallons) -> liters per 100 km. */
export const mpgToL100km = (mpg: number) => (mpg > 0 ? 235.214583 / mpg : 0);

// --- Formatters (return "—" for null/undefined) ---

function round(n: number, decimals = 1): number {
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

/** Format an inches measurement for length/width/height/wheelbase. */
export function formatLength(inches: number | null | undefined, unit: UnitSystem): string {
  if (inches == null) return "—";
  return unit === "metric" ? `${round(inToCm(inches))} cm` : `${round(inches)} in`;
}

/** Format a pounds weight (curb weight, towing, payload, GCWR). */
export function formatWeight(lbsVal: number | null | undefined, unit: UnitSystem): string {
  if (lbsVal == null) return "—";
  return unit === "metric"
    ? `${Math.round(lbsToKg(lbsVal)).toLocaleString()} kg`
    : `${Math.round(lbsVal).toLocaleString()} lbs`;
}

/** Format a miles distance/range figure. */
export function formatDistance(miles: number | null | undefined, unit: UnitSystem): string {
  if (miles == null) return "—";
  return unit === "metric" ? `${Math.round(milesToKm(miles))} km` : `${miles} mi`;
}

/** Format an EPA-style MPG figure, converting to L/100km when in metric mode. */
export function formatMpg(mpg: number | null | undefined, unit: UnitSystem): string {
  if (mpg == null) return "—";
  return unit === "metric" ? `${round(mpgToL100km(mpg))} L/100km` : `${mpg} MPG`;
}

/** Bare number variant of formatMpg for table cells that already say "MPG"/"L/100km" in the header. */
export function formatMpgValue(mpg: number | null | undefined, unit: UnitSystem): string {
  if (mpg == null) return "—";
  return unit === "metric" ? `${round(mpgToL100km(mpg))}` : `${mpg}`;
}
